import {ComponentProps, useCallback} from 'react';
import {useNavigate, Link} from '@shopify/hydrogen';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import 'sweetalert2/dist/sweetalert2.min.css';
import {createPath} from 'history';

const SwalComponent = withReactContent(
  Swal.mixin({
    showClass: {
      popup: 'animate__animated animate__fadeInUp',
    },
    hideClass: {
      popup: 'animate__animated animate__fadeOutUp',
    },
    customClass: {
      title: 'swal2-title',
      actions: 'buttons-container',
      confirmButton: 'twl-button button button_size_large',
    },
    showCancelButton: false,
    showConfirmButton: false,
  }),
);

type SwalLinkProps = {
  navigate: ReturnType<typeof useNavigate>;
} & Pick<
  ComponentProps<typeof Link>,
  'to' | 'replace' | 'clientState' | 'reloadDocument'
> &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

// NOTE: this implementation is a much simplified copy/paste
//       from Hydrogen's Link component, but allows navigate
//       to be passed in as a prop
export function SwalLink({
  navigate,
  to,
  replace: _replace,
  clientState,
  reloadDocument,
  onClick,
  children,
  target,
  ...restProps
}: SwalLinkProps) {
  const internalClick = useCallback(
    (e) => {
      if (onClick) onClick(e);
      if (
        !reloadDocument && // do regular browser stuff
        e.button === 0 && // Ignore everything but left clicks
        (!target || target === '_self') && // Let browser handle "target=_blank"
        !isModifiedEvent(e) // Ignore modifier key clicks
      ) {
        e.preventDefault();

        // If the URL hasn't changed, the regular <a> will do a replace
        const replace =
          !!_replace ||
          createPath(window.location) === createPath({pathname: to});

        navigate(to, {
          replace,
          clientState,
        });
      }
    },
    [reloadDocument, target, _replace, to, clientState, onClick, navigate],
  );

  return (
    <a href={to} onClick={internalClick} {...restProps}>
      {children}
    </a>
  );
}

export default SwalComponent;

function isModifiedEvent(event: React.MouseEvent) {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}
